import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Box,
  HStack,
  VStack,
  Text,
  Icon,
  keyframes,
  Flex,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { Plus, X } from "lucide-react";

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const scaleIn = keyframes`
  from { transform: scale(0); }
  to { transform: scale(1); }
`;

const PopupModelAdd = ({
  isOpen,
  onClose,
  value,
  setValue,
  handleAdd,
  title = "Create Category",
  placeholder = "Enter category name",
  buttonText = "Create",
}: {
  isOpen: any;
  onClose: any;
  value: string;
  setValue: any;
  handleAdd: () => void;
  title?: string;
  placeholder?: string;
  buttonText?: string;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await handleAdd();
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && value.trim()) {
      handleSubmit();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="md"
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent
        borderRadius="3xl"
        overflow="hidden"
        boxShadow="dark-lg"
        mx={4}
        animation={`${slideUp} 0.3s cubic-bezier(0.4, 0, 0.2, 1)`}
        border="1px solid"
        borderColor="blue.100"
      >
        {/* Header - Compact Design */}
        <ModalHeader p={0} position="relative">
          <Flex align="center" justify="space-between" px={6} pt={5} pb={4}>
            {/* Icon with animated background */}
            <Flex align="center" gap={3}>
              <Box
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  position="absolute"
                  w="48px"
                  h="48px"
                  bg="blue.100"
                  borderRadius="full"
                  opacity={0.6}
                  animation={`${scaleIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)`}
                />
                <Box
                  bg="blue.500"
                  borderRadius="full"
                  p={2.5}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                  boxShadow="0 4px 14px 0 rgba(59, 130, 246, 0.4)"
                  animation={`${scaleIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`}
                >
                  <Icon as={Plus} w={5} h={5} color="white" />
                </Box>
              </Box>
              <Box>
                <Text
                  fontSize="lg"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="tight"
                >
                  {title}
                </Text>
                <Text fontSize="xs" color="gray.500" fontWeight="500">
                  Add a new item to your collection
                </Text>
              </Box>
            </Flex>

            {/* Close button */}
            <Box
              as="button"
              onClick={onClose}
              p={1.5}
              borderRadius="lg"
              transition="all 0.2s"
              _hover={{ bg: "gray.100" }}
              _active={{ bg: "gray.200" }}
              disabled={isLoading}
            >
              <Icon as={X} w={5} h={5} color="gray.400" />
            </Box>
          </Flex>
        </ModalHeader>

        {/* Body - Clean Input */}
        <ModalBody px={6} py={4}>
          <VStack spacing={4} align="stretch">
            {/* Input with label */}
            <FormControl>
              <FormLabel
                fontSize="sm"
                fontWeight="600"
                color="gray.700"
                mb={2}
                textTransform="uppercase"
                letterSpacing="0.5px"
              >
                Name
              </FormLabel>
              <Input
                placeholder={placeholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyPress={handleKeyPress}
                size="lg"
                borderRadius="lg"
                border="2px"
                borderColor="gray.200"
                fontSize="sm"
                fontWeight="500"
                _focus={{
                  borderColor: "blue.500",
                  boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                }}
                _placeholder={{
                  color: "gray.400",
                }}
                autoFocus
              />
            </FormControl>

            {/* Help text */}
            <Text fontSize="xs" color="gray.500" fontWeight="500">
              Press Enter or click Create to add a new category
            </Text>
          </VStack>
        </ModalBody>

        {/* Footer - Modern Button Layout */}
        <ModalFooter
          px={6}
          py={4}
          bg="gray.50"
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <HStack spacing={3} w="full">
            <Button
              flex={1}
              variant="ghost"
              size="md"
              onClick={onClose}
              isDisabled={isLoading}
              fontSize="sm"
              fontWeight="600"
              h="44px"
              borderRadius="xl"
              color="gray.700"
              _hover={{
                bg: "gray.200",
              }}
              _active={{
                bg: "gray.300",
              }}
            >
              Cancel
            </Button>
            <Button
              flex={1}
              size="md"
              onClick={handleSubmit}
              isLoading={isLoading}
              isDisabled={!value.trim() || isLoading}
              loadingText="Creating..."
              fontSize="sm"
              fontWeight="700"
              h="44px"
              borderRadius="xl"
              bg="blue.500"
              color="white"
              leftIcon={<Icon as={Plus} w={4} h={4} />}
              _hover={{
                bg: "blue.600",
                transform: "translateY(-1px)",
                boxShadow: "0 8px 16px -4px rgba(59, 130, 246, 0.4)",
              }}
              _active={{
                transform: "translateY(0)",
                bg: "blue.700",
              }}
              transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
              boxShadow="0 4px 12px -2px rgba(59, 130, 246, 0.3)"
            >
              {buttonText}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PopupModelAdd;
